import {
  MCPSidebarTabs,
  GlobalSidebarTabs,
  type SidebarTabs
} from '../sidebar';

/**
 * Enum representing the different user roles in the system.
 *
 * @enum {string}
 * @property {string} ADMIN - Represents an administrator with full access.
 * @property {string} MEMBER - Represents a regular member with standard access.
 */
export enum UserRole {
  ADMIN = 'ADMIN',
  /**
   * For creation of managed connections without full ADMIN permissions
   */
  APP_ADMIN = 'APP_ADMIN',
  // This role is intended to be read-only and limited write
  // (i.e. managing personal connections) for AI product.
  MEMBER = 'MEMBER'
}

type OperationPermissions = {
  /** whether the operation is allowed by default */
  defaultAllow: boolean;
  /** exceptions for the defaultAllow behavior **/
  exceptions: string[];
};

/**
 * Defines the permissions for different user roles within the GraphQL server.
 *
 * @constant
 * @type {Object}
 * @property {Object} UserRole.MEMBER - Permissions for the member role.
 * @property {boolean} UserRole.MEMBER.defaultAllow - Indicates whether mutations are allowed by default for the member role.
 * @property {string[]} UserRole.MEMBER.exceptions - Specific mutations that are allowed for the member role, despite the default setting.
 */
export type RolePermissions = {
  // permissions for queries, mutations, and subscriptions
  query: OperationPermissions;
  mutation: OperationPermissions;
  subscription: OperationPermissions;
  // What sidebar tabs user can access
  sidebarTabs: SidebarTabs[];
};

export const rolePermissions: Record<UserRole, RolePermissions> = {
  [UserRole.ADMIN]: {
    query: {
      defaultAllow: true, // allow all queries by default
      exceptions: []
    },
    mutation: {
      defaultAllow: true, // allow all mutations by default
      exceptions: []
    },
    subscription: {
      defaultAllow: false, // deny all subscriptions by default
      exceptions: []
    },
    sidebarTabs: [
      ...Object.values(MCPSidebarTabs),
      ...Object.values(GlobalSidebarTabs)
    ]
  },
  [UserRole.APP_ADMIN]: {
    query: {
      defaultAllow: true, // allow all queries by default
      exceptions: []
    },
    mutation: {
      defaultAllow: false, // deny all mutations by default
      exceptions: [
        'authorizeServerInstall',
        'createAgent',
        'createCredential',
        'createOrUpdateAccessPolicy',
        'createOrUpdateServerInstall',
        'createShadowMCPServers',
        'deleteAccessPolicy',
        'deleteAgent',
        'editMcpServer',
        'enableMcpServer',
        'markUserConfigSeen',
        'removeMCPServer',
        'deleteMCPServerInstall',
        'requestMCPServerTemplate',
        'setSelectedMCPProfileForUser',
        'removeSelectedMCPProfileForUser',
        'updateAccessPolicy',
        'updateAgent',
        'updateCredentialStatus',
        'updateMCPServerInstall',
        'updateShadowMCPServers',
        'updateMcpServerInstallTools'
      ]
    },
    subscription: {
      defaultAllow: false, // deny all subscriptions by default
      exceptions: []
    },
    sidebarTabs: [
      MCPSidebarTabs.APPS,
      MCPSidebarTabs.MY_CONNECTIONS,
      MCPSidebarTabs.MY_AGENTS,
      GlobalSidebarTabs.DOCUMENTATION
    ]
  },
  [UserRole.MEMBER]: {
    query: {
      defaultAllow: true, // allow all queries by default
      exceptions: []
    },
    mutation: {
      defaultAllow: false, // deny all mutations by default
      exceptions: [
        'authorizeServerInstall',
        'copyMCPProfileToPersonal',
        'createAgent',
        'createCredential',
        'createMCPProfile',
        'createOrUpdateServerInstall',
        'createShadowMCPServers', // Allow desktop apps to report shadow servers
        'deleteAgent',
        'deleteMCPProfile',
        'markUserConfigSeen',
        'deleteMCPServerInstall',
        'setSelectedMCPProfileForUser',
        'removeSelectedMCPProfileForUser',
        'updateAgent',
        'updateCredentialStatus',
        'updateMCPProfile',
        'updateMCPServerInstall',
        'updateMcpServerInstallTools'
      ]
    },
    subscription: {
      defaultAllow: false, // deny all subscriptions by default
      exceptions: []
    },
    sidebarTabs: [
      MCPSidebarTabs.MY_CONNECTIONS,
      MCPSidebarTabs.MY_AGENTS,
      GlobalSidebarTabs.DOCUMENTATION
    ]
  }
};
